/**
 * ! IMPORTANT !
 * This enum must contain the same set of values as packages/shared/consts/permissions.ts.
 * When adding or removing a permission here, mirror the change there (and vice versa).
 *
 * SA rule:
 *   - In forms:      role === ClientOperator OR hasAllPermissions(permissions)
 *   - For logged-in user display/guards: hasAllPermissions(user.profile.permissions)
 *
 * CanImpersonate gates the workspace-switch / impersonation feature (WorkspacesPopoverSA).
 * It is NOT an SA proxy — do not use it for super-admin checks.
 */
export enum Permission {
  CanImpersonate = 'can-impersonate',
  CreateClient = 'create-client',
  CreateCreditInvoice = 'create-credit-invoice',
  CreateCreditMemo = 'create-credit-memo',
  CreateCustomer = 'create-customer',
  CreateDebitInvoice = 'create-debit-invoice',
  CreateReport = 'create-report',
  CreateUser = 'create-user',
  CreateWebhooks = 'create-webhooks',
  CreatePayment = 'create-payment',
  DeleteClient = 'delete-client',
  DeleteCustomer = 'delete-customer',
  DeleteInvoice = 'delete-invoice',
  DeleteReport = 'delete-report',
  DeleteUser = 'delete-user',
  DeleteWebhook = 'delete-webhook',
  DeleteWebhookHistory = 'delete-webhook-history',
  DownloadReport = 'download-report',
  MatchPayment = 'match-payment',
  ReadClient = 'read-client',
  ReadClientPerformanceDashboard = 'read-client-performance-dashboard',
  ReadCustomer = 'read-customer',
  ReadDashboard = 'read-dashboard',
  ReadInvoice = 'read-invoice',
  ReadOidcClient = 'read-oidc-client',
  ResetOidcSecret = 'reset-oidc-secret',
  ReadPayment = 'read-payment',
  ReadSettings = 'read-settings',
  ReadUser = 'read-user',
  ReadWebhooks = 'read-webhooks',
  ReadWebhooksHistory = 'read-webhooks-history',
  ReadReport = 'read-report',
  RetryWebhook = 'retry-webhook',
  UpdateClient = 'update-client',
  UpdateCustomer = 'update-customer',
  UpdateInvoice = 'update-invoice',
  UpdateUser = 'update-user',
  UpdateWebhooks = 'update-webhooks',
  UpdatePayment = 'update-payment',
  GoodwillWaive = 'goodwill-waive',
  DirectPayment = 'direct-payment',
  CancelInvoice = 'cancel-invoice',
  CreditLoss = 'credit-loss',
  PostponeDueDate = 'postpone-due-date',
  AdjustDueDate = 'adjust-due-date',
  DcStop = 'dc-stop',
  ReminderStop = 'reminder-stop',
  CreatePayout = 'create-payout',
  UpdatePayout = 'update-payout',
  ReadPayout = 'read-payout',
  ReadEmail = 'read-email',
  ArchiveEmail = 'archive-email',
  DeleteEmail = 'delete-email',
  ReadIngestion = 'read-ingestion',
  ScheduledPauseManage = 'scheduled-pause-manage',
  WriteIngestion = 'write-ingestion',
  ResendInvoice = 'resend-invoice',
  ReplaceInvoice = 'replace-invoice',
  BackdatePayment = 'backdate-payment',
  ReversePaymentAllocation = 'reverse-payment-allocation',
  ResetStage = 'reset-stage',
}

export enum SupportedRoles {
  ClientOperator = 'client-operator',
  ClientManager = 'client-manager',
  CustomerOperator = 'customer-operator',
  KeyAccountManager = 'key-account-manager',
  Custom = 'custom',
}

export const rolePermissions: Record<Exclude<SupportedRoles, 'custom'>, Permission[]> = {
  'client-manager': [
    Permission.ReadUser,
    Permission.ReadCustomer,
    Permission.UpdateCustomer,
    Permission.ReadInvoice,
    Permission.UpdateInvoice,
    Permission.ReadPayment,
    Permission.MatchPayment,
    Permission.CreateReport,
    Permission.DownloadReport,
    Permission.DeleteReport,
    Permission.ReadReport,
    Permission.ReadDashboard,
    Permission.CreateWebhooks,
    Permission.UpdateWebhooks,
    Permission.DeleteWebhook,
    Permission.DeleteWebhookHistory,
    Permission.ReadWebhooks,
    Permission.ReadWebhooksHistory,
    Permission.RetryWebhook,
    Permission.ReadSettings,
    Permission.ReadOidcClient,
    Permission.ResetOidcSecret,
    Permission.GoodwillWaive,
    Permission.DirectPayment,
    Permission.UpdatePayment,
    Permission.CreatePayment,
    Permission.CancelInvoice,
    Permission.CreditLoss,
    Permission.CreateCreditMemo,
    Permission.PostponeDueDate,
    Permission.AdjustDueDate,
    Permission.DcStop,
    Permission.ReminderStop,
    Permission.CreatePayout,
    Permission.UpdatePayout,
    Permission.ReadPayout,
    Permission.ReadEmail,
    Permission.ArchiveEmail,
    Permission.DeleteEmail,
    Permission.ReadIngestion,
    Permission.WriteIngestion,
    Permission.BackdatePayment,
    Permission.ReversePaymentAllocation,
    Permission.ResetStage,
  ],
  'customer-operator': [
    Permission.CreateCustomer,
    Permission.ReadCustomer,
    Permission.UpdateCustomer,
    Permission.DeleteCustomer,
    Permission.CreateCreditInvoice,
    Permission.CreateDebitInvoice,
    Permission.ReadInvoice,
    Permission.UpdateInvoice,
    Permission.ReadPayment,
    Permission.MatchPayment,
    Permission.CreateReport,
    Permission.DownloadReport,
    Permission.DeleteReport,
    Permission.ReadReport,
    Permission.CreateWebhooks,
    Permission.UpdateWebhooks,
    Permission.ReadWebhooks,
    Permission.ReadWebhooksHistory,
    Permission.RetryWebhook,
    Permission.DeleteWebhook,
    Permission.DeleteWebhookHistory,
    Permission.GoodwillWaive,
    Permission.DirectPayment,
    Permission.UpdatePayment,
    Permission.CreatePayment,
    Permission.CancelInvoice,
    Permission.CreditLoss,
    Permission.CreateCreditMemo,
    Permission.PostponeDueDate,
    Permission.AdjustDueDate,
    Permission.DcStop,
    Permission.ReminderStop,
    Permission.CreatePayout,
    Permission.UpdatePayout,
    Permission.ReadPayout,
    Permission.ReadEmail,
    Permission.ArchiveEmail,
    Permission.DeleteEmail,
    Permission.ReadIngestion,
    Permission.WriteIngestion,
    Permission.BackdatePayment,
    Permission.ReversePaymentAllocation,
    Permission.ResetStage,
  ],
  'key-account-manager': [Permission.ReadClientPerformanceDashboard],
  'client-operator': Object.values(Permission), // superadmin (all permissions),
};
